import React, { forwardRef } from "react";

// @mui material components
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 2 PRO React components
import { Icon, IconButton } from "@mui/material";
import { lightBlue, orange, red, green } from "@mui/material/colors";
import lod_ from "lodash";

import TimeAgo from "javascript-time-ago";
import fr from "javascript-time-ago/locale/fr";
import ReactTimeAgo from "react-time-ago";
import { Box, Typography } from "@material-ui/core";
import { translate } from "helpers/i18n";

/**
 * @typedef {Object} NotificationItemProps
 * @property {string} id - Identifiant de la notification
 * @property {string} [icon] - Nom de l'icône MUI
 * @property {Object} message - Contenu de la notification
 * @property {string} message.title - Titre de la notification
 * @property {string} message.content - Contenu de la notification
 * @property {string} createdAt - Date de création
 * @property {string} severity - Niveau de sévérité : 'info' | 'warning' | 'error' | 'success'
 * @property {boolean} read - Indique si la notification a été lue
 * @property {function} [onClick] - Callback au clic sur la notification
 * @property {function} [onDelete] - Callback à la suppression de la notification
 * @property {boolean} [popUp] - Indique si la notification est affichée en pop-up
 */

const NotificationItem = forwardRef(
	(
		{ icon, message, createdAt, id, severity, read, onClick, onDelete, popUp = false, ...rest },
		ref
	) => {
		TimeAgo.addLocale(fr);

		const SEVERITY_COLORS = {
			info: lightBlue[500],
			warning: orange[500],
			error: red[500],
			success: green[500]
		};

		const SEVERITY_ICONS = {
			info: "info",
			warning: "warning",
			error: "error",
			success: "check_circle"
		};

		const severityColor = SEVERITY_COLORS[severity] || lightBlue[500];
		const severityIcon = SEVERITY_ICONS[severity] || "notifications";

		// Extraire le titre et le contenu du message
		const title = lod_.isNil(message) === false && typeof message === "object" ? message.title : "";
		const content =
			lod_.isNil(message) === false && typeof message === "object" ? message.content : message;

		const handleClick = event => {
			// Éviter la propagation si on clique sur le bouton de suppression
			if (event.target.closest(".notification-delete-btn")) {
				return;
			}
			if (typeof onClick === "function" && !lod_.isNil(onClick)) {
				onClick(id);
			}
		};

		const handleDelete = event => {
			event.stopPropagation();
			if (typeof onDelete === "function" && !lod_.isNil(onDelete)) {
				onDelete();
			}
		};

		return (
			<Box
				{...rest}
				ref={ref}
				p={1}
				sx={{
					pointerEvents: "all",
					cursor: "pointer",
					backgroundColor: read === true ? "transparent" : "rgba(25, 118, 210, 0.04)",
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)"
					},
					borderLeft: read === true ? `4px solid transparent` : `4px solid ${severityColor}`
				}}
				onClick={handleClick}
			>
				<Box
					py={0.5}
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					width="100%"
				>
					{/* Icon */}
					<Box display="flex" alignItems="center" sx={{ minWidth: 40 }}>
						<Avatar sx={{ width: 32, height: 32, backgroundColor: severityColor }}>
							<Icon fontSize="small" sx={{ color: "white" }}>
								{icon || severityIcon}
							</Icon>
						</Avatar>
					</Box>

					{/* Content */}
					<Box sx={{ ml: 1.5, flex: 1 }} alignItems="left">
						{title && (
							<Typography variant="subtitle2" color="textPrimary">
								{translate(title)}
							</Typography>
						)}
						{content && (
							<Typography variant="caption" color="textSecondary">
								{translate(content)}
							</Typography>
						)}
					</Box>

					{/* Time */}
					{popUp === false && (
						<Box sx={{ ml: 1, minWidth: 60, textAlign: "right" }}>
							{lod_.isNil(createdAt) === false && (
								<Typography
									variant="caption"
									fontWeight="regular"
									sx={{ opacity: 0.6, whiteSpace: "nowrap" }}
								>
									<ReactTimeAgo date={new Date(createdAt)} />
								</Typography>
							)}
						</Box>
					)}

					{/* Delete Button */}
					{!lod_.isNil(onDelete) && popUp === false && (
						<Box sx={{ ml: 0.5 }}>
							<IconButton
								size="small"
								className="notification-delete-btn"
								onClick={handleDelete}
								sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
							>
								<Icon fontSize="small">close</Icon>
							</IconButton>
						</Box>
					)}
				</Box>
			</Box>
		);
	}
);

export default NotificationItem;
